/**
 * GTM 마케팅 전환 신호 유틸 — 분석 이벤트 표준의 "명시적 예외" 경계
 *
 * 이 모듈은 제품 분석 이벤트 체계(`@zep/logger/tracking`, docs/event-tracking)와
 * 의도적으로 분리된 별도 경로다. 여기의 구매 전환은 제품 분석용 track 이벤트가
 * 아니라 Google Ads 이벤트 스니펫 계약을 따르는 마케팅 전환이기 때문이다.
 *
 * - 스키마는 Google Ads 계약(send_to/value/currency/transaction_id)을
 *   따르며, 제품 EventMap에 등록하지 않는다.
 * - window.dataLayer 직접 접근은 이 모듈 안에만 허용한다. 결제 hook/컴포넌트는
 *   반드시 이 유틸을 통해서만 전환을 전송한다.
 * - 제품 분석 이벤트를 추가할 때는 이 모듈이 아니라 기존 track 표준을 따를 것.
 *
 * 설치된 Google tag가 dataLayer의 gtag command를 소비하는 구조다.
 * provider-neutral facade를 두지 않는 근거는 ADR 참조:
 * https://github.com/zep-us/ADR/blob/main/decisions/web-app/payment-conversion-gtm-only.md
 */

declare global {
  interface Window {
    dataLayer?: unknown[];
  }
}

type GoogleAdsPurchaseConversionParams = {
  value: number;
  currency: string;
  transactionId: string;
};

const GOOGLE_ADS_CORE_PURCHASE_SEND_TO = 'AW-11409990767/BtRQCMep08kcEO_I2sAq';
const PUSHED_STORAGE_KEY_PREFIX = 'google_ads_purchase_conversion_sent:';
const LEGACY_PUSHED_STORAGE_KEY_PREFIX = 'gtm_purchase_pushed:';
const PENDING_AMOUNT_KEY_PREFIX = 'gtm_purchase_amount:';

type GtmPurchaseAmount = {
  value: number;
  currency: string;
};

/**
 * PG 리다이렉트 전에 실제 청구액을 보관한다.
 * (일시납처럼 결제가 풀 리다이렉트를 거치면 인메모리 값이 유실되므로,
 * 완료 페이지가 takeGtmPurchaseAmount로 꺼내 purchase 신호에 사용한다)
 */
export const stashGtmPurchaseAmount = (
  transactionId: string,
  amount: GtmPurchaseAmount,
) => {
  if (typeof window === 'undefined' || !transactionId) return;
  try {
    window.sessionStorage.setItem(
      `${PENDING_AMOUNT_KEY_PREFIX}${transactionId}`,
      JSON.stringify(amount),
    );
  } catch {
    // 보관 실패 시 완료 페이지의 fallback 값(salePrice)이 사용된다.
  }
};

export const takeGtmPurchaseAmount = (
  transactionId: string,
): GtmPurchaseAmount | null => {
  if (typeof window === 'undefined' || !transactionId) return null;
  try {
    const raw = window.sessionStorage.getItem(
      `${PENDING_AMOUNT_KEY_PREFIX}${transactionId}`,
    );
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof (parsed as GtmPurchaseAmount)?.value !== 'number' ||
      typeof (parsed as GtmPurchaseAmount)?.currency !== 'string'
    ) {
      return null;
    }
    return parsed as GtmPurchaseAmount;
  } catch {
    return null;
  }
};

let hasGrantedGtmConsent = false;

/**
 * 동의(grant) 전에 발생한 구매 전환 보류 큐.
 *
 * conversion payload(value/currency/transaction_id)는 동의 확인 전에는
 * dataLayer에 넣지 않는다. 다만 React 이펙트 순서상 완료 페이지(자식)의
 * push가 AcceptCookieWrapper(부모)의 동의 확인보다 먼저 실행될 수 있어,
 * 즉시 버리는 대신 여기 보류했다가 grant 시점에 flush한다.
 * 동의가 끝내 grant되지 않으면(EU 미수락 등) 어디에도 push되지 않는다.
 */
const pendingPurchaseConversions: GoogleAdsPurchaseConversionParams[] = [];

function pushGtagCommand(..._args: unknown[]) {
  try {
    window.dataLayer = window.dataLayer ?? [];
    window.dataLayer.push(arguments);
    return true;
  } catch {
    // 마케팅 태그 오류가 결제 성공 흐름에 영향을 주지 않도록 격리한다.
    return false;
  }
}

const pushGoogleAdsPurchaseConversionToDataLayer = ({
  value,
  currency,
  transactionId,
}: GoogleAdsPurchaseConversionParams) => {
  const storageKey = `${PUSHED_STORAGE_KEY_PREFIX}${transactionId}`;
  const legacyStorageKey = `${LEGACY_PUSHED_STORAGE_KEY_PREFIX}${transactionId}`;
  try {
    if (
      window.sessionStorage.getItem(storageKey) ||
      window.sessionStorage.getItem(legacyStorageKey)
    ) {
      return true;
    }
  } catch {
    // sessionStorage 접근 불가(시크릿 모드 등) 시 dedupe 없이 진행.
    // Google Ads는 transaction_id로 서버에서 한 번 더 dedupe한다.
  }

  // Google의 이벤트 스니펫과 동일하게 arguments 형태의 gtag command를
  // dataLayer에 넣는다. 설치된 Google tag가 이 command를 소비해 전환을 보낸다.
  const didPush = pushGtagCommand('event', 'conversion', {
    send_to: GOOGLE_ADS_CORE_PURCHASE_SEND_TO,
    value,
    currency,
    transaction_id: transactionId,
  });
  if (!didPush) return false;

  try {
    window.sessionStorage.setItem(storageKey, '1');
  } catch {
    // 기록 실패 시 Google Ads transaction_id dedupe에 맡긴다.
  }

  return true;
};

const flushPendingPurchaseConversions = () => {
  while (pendingPurchaseConversions.length > 0) {
    const pending = pendingPurchaseConversions[0];
    if (!pending) return;

    const didPush = pushGoogleAdsPurchaseConversionToDataLayer(pending);
    if (!didPush) return;

    pendingPurchaseConversions.shift();
  }
};

/**
 * GTM Consent Mode 동의 상태를 granted로 업데이트한다.
 *
 * _document.tsx의 GTM 스니펫이 기본값을 denied로 초기화하므로,
 * 기존 쿠키 동의 흐름(AcceptCookieWrapper.onAcceptedCookie: EU는 배너 수락 시,
 * 비EU는 mount 시 자동 호출)에서 이 함수를 호출해 동의 이후에만
 * 광고/분석 storage 동작을 허용한다. grant 시 동의 전 보류된 구매 전환도
 * 함께 flush한다.
 */
export const grantGtmMarketingConsent = () => {
  if (typeof window === 'undefined') return;

  if (hasGrantedGtmConsent) {
    flushPendingPurchaseConversions();
    return;
  }

  // GTM consent API는 배열이 아니라 arguments 객체 push를 요구한다.
  const didPushConsent = pushGtagCommand('consent', 'update', {
    ad_storage: 'granted',
    ad_user_data: 'granted',
    ad_personalization: 'granted',
    analytics_storage: 'granted',
  });
  if (!didPushConsent) return;

  hasGrantedGtmConsent = true;
  flushPendingPurchaseConversions();
};

/**
 * 결제 완료 시 Google Ads 젭 코어 구매 전환 이벤트를 직접 전송한다.
 *
 * Google 지원 케이스에서 제공한 이벤트 스니펫의 send_to를 사용하며,
 * 실제 결제 금액/통화/서버 주문 ID를 동적으로 전달한다.
 *
 * 동의(grantGtmMarketingConsent) 전에는 payload를 dataLayer에 넣지 않고
 * 보류 큐에만 담는다 (grant 시 flush, 미동의 시 폐기).
 * 새로고침/재방문으로 같은 주문이 중복 집계되지 않도록
 * transactionId 기준으로 세션당 1회만 push한다.
 */
export const pushGoogleAdsPurchaseConversion = (
  params: GoogleAdsPurchaseConversionParams,
) => {
  if (typeof window === 'undefined') return;
  if (process.env.NEXT_PUBLIC_STAGE !== 'PRD') return;
  if (
    process.env.NEXT_PUBLIC_VERCEL_ENV === 'preview' ||
    window.location?.hostname?.endsWith('.preview.zep.us')
  ) {
    return;
  }

  const { value, currency, transactionId } = params;
  if (!transactionId || !currency || !Number.isFinite(value) || value <= 0) {
    return;
  }

  if (!hasGrantedGtmConsent) {
    pendingPurchaseConversions.push(params);
    return;
  }

  // 이미 grant된 뒤의 전환도 큐를 거쳐 전송한다. 일시적인 dataLayer 실패가
  // 있었다면 다음 결제 전환 호출에서 앞서 보존한 항목부터 다시 시도한다.
  pendingPurchaseConversions.push(params);
  flushPendingPurchaseConversions();
};

// 기존 결제 호출부의 API를 유지하되 실제 출력은 Google Ads conversion이다.
export const pushGtmPurchaseEvent = pushGoogleAdsPurchaseConversion;
